import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, SectionLabel, Button, Input, Field } from "./ui";
import { cn } from "../lib/utils";
import { HardDrive, CheckCircle2, AlertTriangle, Lock, Trash2 } from "lucide-react";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);
const EMPTY = { account_id: "", access_key_id: "", secret_access_key: "", bucket: "", endpoint: "" };

export default function StoragePanel() {
  const { lang, toast } = useApp();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/storage");
    setStatus(data);
    if (!data.configured) setEditing(true);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.put("/admin/storage", form);
      setStatus(data); setEditing(false); setForm(EMPTY);
      toast(L(lang, "Stockage connecté et vérifié.", "Storage connected and verified."), { variant: "success" });
    } catch (e) {
      setError(e?.response?.data?.detail || L(lang, "Échec de la validation.", "Validation failed."));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/storage");
      await load(); setEditing(true); setForm(EMPTY);
      toast(L(lang, "Stockage déconnecté.", "Storage disconnected."), { variant: "success" });
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const fields = [
    { k: "account_id", label: L(lang, "ID de compte Cloudflare (R2 uniquement)", "Cloudflare Account ID (R2 only)"), ph: "a1b2c3d4..." },
    { k: "bucket", label: L(lang, "Nom du bucket", "Bucket name"), ph: "cockpit-files" },
    { k: "access_key_id", label: "Access Key ID", ph: "AKIA..." },
    { k: "secret_access_key", label: "Secret Access Key", ph: "••••••••", type: "password" },
    { k: "endpoint", label: L(lang, "Endpoint S3 (R2: optionnel · S3/MinIO: requis)", "S3 endpoint (R2: optional · S3/MinIO: required)"), ph: "https://<account>.r2.cloudflarestorage.com", full: true },
  ];

  return (
    <Card className="mb-6" data-testid="storage-panel">
      <div className="flex items-center justify-between gap-4">
        <SectionLabel>{L(lang, "Stockage de fichiers (R2 / S3)", "File storage (R2 / S3)")}</SectionLabel>
        {status.configured ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-500" data-testid="storage-status-connected">
            <CheckCircle2 className="w-4 h-4" /> {L(lang, "Connecté", "Connected")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-500" data-testid="storage-status-unconfigured">
            <AlertTriangle className="w-4 h-4" /> {L(lang, "Non configuré", "Not configured")}
          </span>
        )}
      </div>

      <p className="text-sm text-text-secondary mt-2 mb-4 flex items-start gap-1.5">
        <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-text-secondary" />
        {L(lang, "Vos identifiants sont chiffrés et ne quittent jamais le serveur. Les fichiers de chaque espace de travail sont cloisonnés par des clés d'objet préfixées et des contrôles d'accès.",
                 "Your credentials are encrypted and never leave the server. Each workspace's files are isolated via prefixed object keys and access checks.")}
      </p>

      {status.configured && !editing ? (
        <div className="space-y-2">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-text-secondary">{L(lang, "Bucket", "Bucket")}: </span><span className="text-text-primary font-mono">{status.bucket}</span></div>
            <div><span className="text-text-secondary">Account: </span><span className="text-text-primary font-mono">{status.account_id}</span></div>
            <div className="sm:col-span-2"><span className="text-text-secondary">Access Key: </span><span className="text-text-primary font-mono">{status.access_key_id_masked}</span></div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="secondary" onClick={() => { setEditing(true); setForm(EMPTY); }} data-testid="storage-edit">
              <HardDrive className="w-4 h-4" /> {L(lang, "Modifier", "Update")}
            </Button>
            <Button variant="secondary" onClick={disconnect} disabled={busy} data-testid="storage-disconnect"
              className="text-red-400 hover:text-red-300">
              <Trash2 className="w-4 h-4" /> {L(lang, "Déconnecter", "Disconnect")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.k} className={f.full ? "sm:col-span-2" : ""}>
                <Field label={f.label}>
                  <Input type={f.type || "text"} value={form[f.k]} onChange={set(f.k)} placeholder={f.ph}
                    autoComplete="off" data-testid={`storage-field-${f.k}`} />
                </Field>
              </div>
            ))}
          </div>
          {error && (
            <div className="text-sm text-red-400 flex items-start gap-1.5" data-testid="storage-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy} data-testid="storage-save">
              {busy ? L(lang, "Vérification…", "Verifying…") : L(lang, "Tester et enregistrer", "Test & Save")}
            </Button>
            {status.configured && (
              <Button variant="secondary" onClick={() => { setEditing(false); setError(""); }} disabled={busy} data-testid="storage-cancel">
                {L(lang, "Annuler", "Cancel")}
              </Button>
            )}
          </div>
          <p className="text-[11px] text-text-secondary">
            {L(lang, "Compatible Cloudflare R2, AWS S3 ou MinIO (auto-hébergé). Pour R2, indiquez l'ID de compte ; pour S3/MinIO, indiquez l'endpoint. La connexion est testée (écriture + suppression) avant l'enregistrement.",
                     "Works with Cloudflare R2, AWS S3, or MinIO (self-hosted). For R2, provide the Account ID; for S3/MinIO, provide the endpoint. The connection is tested (write + delete) before saving.")}
          </p>
        </div>
      )}
    </Card>
  );
}
